import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { filterToValidAttributes } from '@zocdoc/dom-property';

import { breakpoint, columnSize, ColumnAndRow } from '../grid';
import Close from '../icons/Close';
import { injectKeyHandler, Keys } from '../KeyHandler';
import { ScrollLock } from '../ScrollLock';

const shouldUsePortal = () => typeof document !== 'undefined' && !global.screenshot;

class ModalView extends PureComponent {
    static propTypes = {
        onClickCancel: PropTypes.func,
        // Turn on to force user to click the x or press escape to close the modal. Defaults to false
        ignoreClickOutside: PropTypes.bool,
    };

    static defaultProps = {
        ignoreClickOutside: false,
    };

    getOrCreateElementForPortal = () => {
        if (!this.elementForPortal) {
            this.elementForPortal = document.createElement('div');
            this.elementForPortal.id = 'modal-root';
        }

        return this.elementForPortal;
    }

    componentDidMount() {
        if (shouldUsePortal() && !this.elementForPortal) {
            const el = this.getOrCreateElementForPortal();
            document.body.appendChild(el);
            this.forceUpdate();
        }
    }

    componentWillUnmount() {
        if (shouldUsePortal() && this.elementForPortal) {
            document.body.removeChild(this.elementForPortal);
        }
    }

    render() {
        const MainElement = this.elementForPortal ? Portal : 'div';
        const props = this.elementForPortal ? { rootElement: this.elementForPortal } : {};

        return (
            <MainElement {...props}>
                <div {...filterToValidAttributes(this.props)}>
                    <StyledOverlay data-test="modal-body-overlay" />

                    <StyledContainer data-test="modal-container">
                        <StyledClickLayer
                            data-test="modal-fixed"
                            onClick={this.handleClickOutside}
                        >
                            <StyledModal data-test="modal-content">
                                {this.maybeRenderCloseIcon()}
                                {this.props.children}
                            </StyledModal>
                        </StyledClickLayer>
                    </StyledContainer>
                </div>
                <ScrollLock />
            </MainElement>
        );
    }

    maybeRenderCloseIcon() {
        if (this.props.onClickCancel) {
            return (
                <StyledClose
                    data-test="close-icon"
                    height={16}
                    onClick={this.props.onClickCancel}
                />
            );
        }
    }

    handleClickOutside = e => {
        const { ignoreClickOutside, onClickCancel } = this.props;

        if (!ignoreClickOutside && onClickCancel && e.target === e.currentTarget) {
            onClickCancel();
        }
    };
}

const Portal = ({ children, rootElement }) => ReactDOM.createPortal(
    <div>{children}</div>, // enzyme tests were only passing with this added div
    rootElement
);

const Modal = injectKeyHandler({
    [Keys.ESCAPE]: props => {
        if (props.onClickCancel) {
            props.onClickCancel();
        }
    },
})(ModalView);

const StyledModal = styled.div`
    ${columnSize(24)}
    background-color: #FFF;
    bottom: 0;
    box-sizing: border-box;
    float: left;
    left: 0;
    max-height: 75vh;
    overflow: auto;
    padding: 40px 16px 18px;
    position: fixed;
    z-index: 12;

    ${breakpoint.small} {
        ${columnSize(22)}
        box-sizing: border-box;
        height: auto;
        min-width: 586px;
        max-width: 800px;
        padding: 44px 70px;
        position: relative;
    }
`;

const StyledOverlay = styled.div`
    background-color: #000;
    opacity: 0.6;
    position: fixed;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    visibility: visible;
    z-index: 11;
`;

const StyledClickLayer = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 12;
`;

const StyledContainer = styled(ColumnAndRow)`
    box-sizing: border-box;
    position: relative;
`;

const StyledClose = styled(Close)`
    cursor: pointer;
    position: absolute;
    right: 16px;
    top: 16px;
`;

export {
    Modal as default,
    Modal,
    ModalView,
    StyledModal as ModalContent,
    StyledOverlay as ModalOverlay,
};
